import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { incluirCargaManualProgramacaoHavan, listarFiliaisProgramacaoHavan } from '../../services/programacaoHavanService';
import { formatarDataBr } from '../utils/dataBr';

// Tipos de caminhão que vêm nas planilhas: sugestão ao digitar (pode digitar outro).
const TIPOS_CAMINHAO = ['Toco 24', 'Truck 40', 'Carreta 56'];

const formularioVazio = turno => ({
  numero_carga: '',
  filial_codigo: '',
  turno: String(turno || 1),
  box: '',
  tipo_caminhao: '',
  volume_carga: '',
  inicio_carregamento: '',
  termino_carregamento: '',
  saida_cdh: '',
  chegada_loja: '',
  restricoes: '',
  observacao: ''
});

const mensagemDoErro = error => error?.response?.data?.error || error?.message || 'Não foi possível incluir a carga.';

/**
 * Inclusão manual de carga na programação do dia selecionado. Obrigatórios: número da carga e filial (o número e a UF
 * da filial vêm da escolha). Motorista e veículos ficam de fora: entram depois, na própria linha da tabela.
 */
const ProgramacaoHavanCargaManualModal = ({ show, dataProgramacao, turnoPadrao, onClose, onIncluida }) => {
  const [formulario, setFormulario] = useState(() => formularioVazio(turnoPadrao));
  const [filiais, setFiliais] = useState([]);
  const [carregandoFiliais, setCarregandoFiliais] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  // Cada abertura começa limpa; as filiais vêm da API na primeira abertura.
  useEffect(() => {
    if (!show) return undefined;
    setFormulario(formularioVazio(turnoPadrao));
    setErro('');
    if (filiais.length) return undefined;
    let ativo = true;
    setCarregandoFiliais(true);
    listarFiliaisProgramacaoHavan()
      .then(lista => { if (ativo) setFiliais(Array.isArray(lista) ? lista : []); })
      .catch(error => { if (ativo) setErro(`Não foi possível carregar as filiais: ${mensagemDoErro(error)}`); })
      .finally(() => { if (ativo) setCarregandoFiliais(false); });
    return () => { ativo = false; };
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const filiaisPorNome = useMemo(() => [...filiais].sort((a, b) => String(a.filial_nome).localeCompare(String(b.filial_nome), 'pt-BR')), [filiais]);
  const filialEscolhida = filiais.find(filial => String(filial.filial_codigo) === String(formulario.filial_codigo));
  const podeIncluir = Boolean(String(formulario.numero_carga).trim()) && Boolean(filialEscolhida) && Boolean(dataProgramacao) && !salvando;

  const alterar = campo => event => setFormulario(atual => ({ ...atual, [campo]: event.target.value }));

  const incluir = async event => {
    event.preventDefault();
    if (!podeIncluir) return;
    setSalvando(true);
    setErro('');
    const vazioViraNulo = valor => (String(valor ?? '').trim() === '' ? null : valor);
    try {
      const resultado = await incluirCargaManualProgramacaoHavan({
        data_programacao: dataProgramacao,
        turno: Number(formulario.turno),
        numero_carga: formulario.numero_carga.trim(),
        filial_codigo: Number(formulario.filial_codigo),
        box: vazioViraNulo(formulario.box),
        tipo_caminhao: vazioViraNulo(formulario.tipo_caminhao),
        volume_carga: vazioViraNulo(formulario.volume_carga) === null ? null : Number(formulario.volume_carga),
        inicio_carregamento: vazioViraNulo(formulario.inicio_carregamento),
        termino_carregamento: vazioViraNulo(formulario.termino_carregamento),
        saida_cdh: vazioViraNulo(formulario.saida_cdh),
        chegada_loja: vazioViraNulo(formulario.chegada_loja),
        restricoes: vazioViraNulo(formulario.restricoes),
        observacao: vazioViraNulo(formulario.observacao)
      });
      onIncluida?.(resultado);
    } catch (error) {
      setErro(mensagemDoErro(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal show={show} onHide={salvando ? undefined : onClose} centered size="lg" animation={false}>
      <Form onSubmit={incluir} noValidate>
        <Modal.Header closeButton={!salvando}>
          <Modal.Title>Adicionar carga · {formatarDataBr(dataProgramacao)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Dica>
            Preencha os dados básicos da carga. Só o <strong>número da carga</strong> e a <strong>filial</strong> são obrigatórios.
            Motorista e veículos são informados depois, na própria linha da tabela.
          </Dica>
          {erro ? <Alert variant="danger" className="py-2">{erro}</Alert> : null}

          <Secao>
            <TituloSecao>Carga</TituloSecao>
            <Grade>
              <Form.Group controlId="cargaManualNumero">
                <Form.Label>Número da carga *</Form.Label>
                <Form.Control size="sm" autoFocus required value={formulario.numero_carga} onChange={alterar('numero_carga')} placeholder="Ex.: 286700" />
                <Form.Text>O "H" no final é colocado automaticamente, como na importação.</Form.Text>
              </Form.Group>
              <Form.Group controlId="cargaManualFilial">
                <Form.Label>Filial *</Form.Label>
                <Form.Select size="sm" required value={formulario.filial_codigo} onChange={alterar('filial_codigo')} disabled={carregandoFiliais}>
                  <option value="">{carregandoFiliais ? 'Carregando filiais…' : 'Selecione a filial'}</option>
                  {filiaisPorNome.map(filial => (
                    <option key={filial.filial_codigo} value={filial.filial_codigo}>{filial.filial_nome}{filial.uf ? ` (${filial.uf})` : ''}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group controlId="cargaManualNumeroFilial">
                <Form.Label>Nº da filial</Form.Label>
                <Form.Control size="sm" readOnly plaintext value={filialEscolhida ? `${filialEscolhida.filial_codigo}${filialEscolhida.uf ? ` · ${filialEscolhida.uf}` : ''}` : 'vem da filial escolhida'} />
              </Form.Group>
              <Form.Group controlId="cargaManualTurno">
                <Form.Label>Turno</Form.Label>
                <Form.Select size="sm" value={formulario.turno} onChange={alterar('turno')}>
                  <option value="1">1º turno</option>
                  <option value="2">2º turno</option>
                </Form.Select>
              </Form.Group>
              <Form.Group controlId="cargaManualBox">
                <Form.Label>Box</Form.Label>
                <Form.Control size="sm" maxLength={50} value={formulario.box} onChange={alterar('box')} />
              </Form.Group>
              <Form.Group controlId="cargaManualTipoCaminhao">
                <Form.Label>Tipo de caminhão</Form.Label>
                <Form.Control size="sm" list="cargaManualTiposCaminhao" maxLength={150} value={formulario.tipo_caminhao} onChange={alterar('tipo_caminhao')} />
                <datalist id="cargaManualTiposCaminhao">{TIPOS_CAMINHAO.map(tipo => <option key={tipo} value={tipo} />)}</datalist>
              </Form.Group>
              <Form.Group controlId="cargaManualVolume">
                <Form.Label>Volume</Form.Label>
                <Form.Control size="sm" type="number" min={0} value={formulario.volume_carga} onChange={alterar('volume_carga')} />
              </Form.Group>
            </Grade>
          </Secao>

          <Secao>
            <TituloSecao>Horários e datas</TituloSecao>
            <Grade>
              <Form.Group controlId="cargaManualInicio">
                <Form.Label>H. cheg. veículo</Form.Label>
                <Form.Control size="sm" type="time" value={formulario.inicio_carregamento} onChange={alterar('inicio_carregamento')} />
              </Form.Group>
              <Form.Group controlId="cargaManualTermino">
                <Form.Label>Término do carregamento</Form.Label>
                <Form.Control size="sm" type="time" value={formulario.termino_carregamento} onChange={alterar('termino_carregamento')} />
              </Form.Group>
              <Form.Group controlId="cargaManualSaida">
                <Form.Label>Saída CDH</Form.Label>
                <Form.Control size="sm" type="date" value={formulario.saida_cdh} onChange={alterar('saida_cdh')} />
                <Form.Text>Vazia: a data da programação.</Form.Text>
              </Form.Group>
              <Form.Group controlId="cargaManualChegadaLoja">
                <Form.Label>Chegada loja</Form.Label>
                <Form.Control size="sm" type="date" value={formulario.chegada_loja} onChange={alterar('chegada_loja')} />
              </Form.Group>
            </Grade>
          </Secao>

          <Secao>
            <TituloSecao>Observações</TituloSecao>
            <Grade>
              <Form.Group controlId="cargaManualRestricoes">
                <Form.Label>Restrições</Form.Label>
                <Form.Control size="sm" value={formulario.restricoes} onChange={alterar('restricoes')} />
              </Form.Group>
              <Form.Group controlId="cargaManualObservacao">
                <Form.Label>Observação</Form.Label>
                <Form.Control size="sm" as="textarea" rows={2} maxLength={500} value={formulario.observacao} onChange={alterar('observacao')} />
              </Form.Group>
            </Grade>
          </Secao>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onClose} disabled={salvando}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={!podeIncluir}>{salvando ? 'Incluindo…' : 'Incluir carga'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

ProgramacaoHavanCargaManualModal.propTypes = {
  show: PropTypes.bool,
  dataProgramacao: PropTypes.string,
  turnoPadrao: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onIncluida: PropTypes.func
};

ProgramacaoHavanCargaManualModal.defaultProps = {
  show: false,
  dataProgramacao: null,
  turnoPadrao: 1,
  onIncluida: undefined
};

export default ProgramacaoHavanCargaManualModal;

const Dica = styled.p`margin: 0 0 12px; color: #526275; font-size: 13px;`;
const Secao = styled.section`& + & { margin-top: 12px; }`;
const TituloSecao = styled.h6`margin: 0 0 6px; color: #163455; font-size: 12px; font-weight: 700; text-transform: uppercase;`;
const Grade = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px 12px;
  label { margin-bottom: 2px; color: #526275; font-size: 12px; font-weight: 600; }
  .form-text { font-size: 11px; }
`;
